import { Component, ReactNode } from 'react';
import { getLoggerInstance } from '../../core/monitoring/logger';
import { detectCommonErrors } from './services/detect-common-errors';

const logger = getLoggerInstance('ErrorBoundary');

type ErrorBoundaryProps = {
  children: ReactNode;
  fallback: (props: FallbackProps) => ReactNode;
};

type FallbackProps = {
  error: Error | null;
};

type ErrorBoundaryState = {
  hasError: boolean;
  error: Error | null;
};

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error) {
    const structuredError = detectCommonErrors(error);
    if (structuredError) {
      logger.track(structuredError.level, structuredError.message, { exception: error });
    } else {
      logger.error(error.message, { exception: error });
    }
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback({ error: this.state.error });
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
